import * as THREE from "three";
import debug from "debug";
import ContextMenuModalUI from "./ContextMenuModalUI";
import { FontLoader } from "three/examples/jsm/loaders/FontLoader.js";
import { TextGeometry } from "three/examples/jsm/geometries/TextGeometry.js";
const log = debug("app:fn");

/**
 * Handles the data layer for the context menu modal.
 * @category Components
 * @subcategory ContextMenu
 */
export class ContextMenuModalData {
  /**
   * Creates an instance of ContextMenuModalData.
   * @param {Object} experience - The experience object containing the event hub and data vault.
   */
  constructor(experience) {
    /**
     * The event hub for managing events.
     * @type {Object}
     */
    this.eventHub = experience.eventHub;

    /**
     * The experience object for accessing scene and other resources.
     * @type {Object}
     */
    this.experience = experience;

    /**
     * Part data storage from the data vault.
     * @type {Map<string, Object>}
     */
    this.partData = experience.dataVault.parts;

    /**
     * UI manager for the context menu modal.
     * @type {ContextMenuModalUI}
     */
    this.contextMenuModalUI = new ContextMenuModalUI(this.eventHub);

    this.init();
  }

  /**
   * Initializes event listeners for the context menu modal.
   */
  init() {
    this.eventHub.on("moreInformation", () =>
      this.showInfoOrConfigure("moreInformation")
    );
    this.eventHub.on("configurePN", () =>
      this.showInfoOrConfigure("configurePN")
    );
    this.eventHub.on("contextMenuSave", this.handleSave.bind(this));
    this.eventHub.on("contextMenuReset", this.resetConfigured.bind(this));
    this.eventHub.on("resetConfigured", this.resetConfigured.bind(this));
  }

  cleanup() {
    console.log("ContextMenuModalData cleanup started...");
  
    // 1. Remove event listeners from eventHub
    if (this.handleMoreInformation) {
      this.eventHub.off("moreInformation", this.handleMoreInformation);
    }
    if (this.handleConfigurePN) {
      this.eventHub.off("configurePN", this.handleConfigurePN);
    }
    if (this.handleContextMenuSave) {
      this.eventHub.off("contextMenuSave", this.handleContextMenuSave);
    }
    if (this.handleContextMenuReset) {
      this.eventHub.off("contextMenuReset", this.handleContextMenuReset);
    }
    if (this.handleResetConfigured) {
      this.eventHub.off("resetConfigured", this.handleResetConfigured);
    }
  
    // 2. Dispose UI instance if applicable
    if (this.contextMenuModalUI && typeof this.contextMenuModalUI.dispose === "function") {
      this.contextMenuModalUI.dispose();
    }
    this.contextMenuModalUI = null;
  
    // 3. Clear internal state and object references
    this.userData = null;
    this.objectId = null;
    this.foundPart = null;
    this.partData = null;
  
    console.log("ContextMenuModalData cleanup completed.");
  }

  /**
   * Retrieves userData, objectId, partNumber, and foundPart for the selected item.
   * 
   * Retrieves selected data including userData, objectId, partNumber, and foundPart.
   * @returns {Object} - The selected data.
   * @property {Object} userData - The user data associated with the selected part.
   * @property {string} objectId - The unique object ID.
   * @property {string} partNumber - The part number.
   * @property {Object|null} foundPart - The part data from the data vault.
   */
  _getSelectedData() {
    const userData = window.selectedForCM?.userData || {};
    const objectId = userData.objectId;
    const partNumber = userData.partNumber;
    const foundPart = partNumber ? this.partData.get(partNumber) : null;
    return { userData, objectId, partNumber, foundPart };
  }

  /**
   * Opens the modal with properties based on part number and initial mode (view-only or editable).
   * @param {string} event - The event triggering this action ("moreInformation" or "configurePN").
   */
  showInfoOrConfigure(event) {
    const { userData, foundPart, partNumber } = this._getSelectedData();
    log("userData, objectId, partNumber", userData, foundPart, partNumber);
    const properties = userData.properties || {};
    this.eventHub.emit("openContextMenuModal", {
      mode: userData.mode,
      properties: properties,
    });
    log(
      `${event} Mode Initialized for Part Number ${partNumber}:`,
      properties,
      foundPart
    );
  }

  /**
   * Resets the properties of the current part to default values and emits a log event.
   */
  resetConfigured() {
    const { userData, objectId, foundPart, partNumber } =
      this._getSelectedData();

    if (userData.mode === "xm-part" || userData.mode === "user-cfg") {
      this.emitResetFailedEvent();
    }

    if (foundPart) {
      const originalProps = { ...userData };
      const props = this._prepareDefaultProperties({}, foundPart);
      const newProps = {
        ...userData,
        properties: props,
      };
      this.eventHub.emit("resetToDefault", props);
      log("reset props", props);
      log("reset newProps", newProps);
      
      // Update visual text meshes immediately
      this.updateTextMeshes(props);
      
      this.emitLogEvent(objectId, "xm-part", originalProps, newProps);
    } else {
      this.emitResetFailedEvent();
    }
  }

  /**
   * Emits a reset failure message to the modal.
   * Retrieves userData, objectId, partNumber, and foundPart for the selected item.
   */
  emitResetFailedEvent() {
    this.eventHub.emit("modalInfo", {
      title: "Reset To Default",
      message:
        "The part may already have original properties, or the configured part cannot be reset to the default state.",
      actions: [
        {
          label: "OK",
          callback: () => {
            log("OK button clicked");
          },
        },
      ],
      autoClose: true,
    });
  }

  /**
   * Saves the modified properties and emits a log event with updated data.
   * @param {Object} params - The parameters for the save action.
   * @param {Object} params.updatedData - The updated properties data.
   */
  handleSave({ updatedData }) {
    const { objectId, userData } = this._getSelectedData();
    const originalProps = { ...userData };
    const updatedUserData = { ...userData, mode: "cfg-part" };
    const newProps = { ...updatedUserData, properties: updatedData };
    log("original props, new props", originalProps, newProps);
    
    // Update visual text meshes immediately
    this.updateTextMeshes(updatedData);
    
    this.emitLogEvent(objectId, "cfg-part", originalProps, newProps);
  }

  /**
   * Helper method to emit a standardized log event.
   * Emits a standardized log event.
   * @param {string} objectId - The unique object ID.
   * @param {string} mode - The mode of the object (e.g., "cfg-part").
   * @param {Object} previousState - The previous state of the object.
   * @param {Object} changedState - The changed state of the object.
   */
  emitLogEvent(objectId, mode, previousState, changedState) {
    this.eventHub.emit("logEvent", {
      eventType: "edit",
      data: { objectId, mode, previousState, changedState },
    });
    this.eventHub.emit("xmd");
  }

  /**
   * Prepares properties by merging existing properties with default values.
   * @param {Object} properties - The existing properties.
   * @param {Object} foundPart - The part data from the data vault.
   * @returns {Object} - The prepared properties.
   */
  _prepareDefaultProperties(properties, foundPart) {
    let props = {
      ...properties,
      "Part Number": foundPart.id,
      Manufacturer: foundPart.mfgCode,
      "Manufacturer Part Number": foundPart.mfgPart,
      "Form Factor": foundPart.ffId,
      "Price (USD)": foundPart.price || "N/A",
    };
    if (foundPart.props) {
      props = foundPart.props.reduce((acc, prop) => {
        acc[`${prop.label}`] = prop.value;
        return acc;
      }, props); // Pass `props` as the initial value to retain existing properties
    }

    return props;
  }

  /**
   * Updates the text meshes (part number and manufacturer part number) on the 3D part.
   * @param {Object} updatedData - The updated properties data.
   */
  async updateTextMeshes(updatedData) {
    try {
      if (!window.selectedForCM) {
        log("No selected object for updating text meshes");
        return;
      }

      // Find the XMO group (the part group)
      // window.selectedForCM should be the selected object from experience.selectedObjects
      let partGroup = window.selectedForCM;
      
      // If it's not the XMO group, try to find it by traversing up the parent chain
      if (partGroup.name !== "XMO") {
        let current = partGroup;
        while (current.parent) {
          if (current.parent.name === "XMO") {
            partGroup = current.parent;
            break;
          }
          current = current.parent;
        }
        
        // If still not found, log and return
        if (partGroup.name !== "XMO") {
          log("Could not find XMO group for updating text meshes. Selected object:", partGroup);
          return;
        }
      }

      const partNumber = updatedData["Part Number"];
      const mfgPart = updatedData["Manufacturer Part Number"];

      if (!partNumber && !mfgPart) {
        log("No part number or manufacturer part number to update");
        return;
      }

      // Find text meshes within the part group
      const textMeshes = {
        partNumber: null,
        mfgPart: null,
      };

      partGroup.traverse((child) => {
        if (child.name === "partNumber" && child.userData?.type === "textGeometry") {
          textMeshes.partNumber = child;
        } else if (child.name === "MfgPart" && child.userData?.type === "textGeometry") {
          textMeshes.mfgPart = child;
        }
      });

      // Update userData on the part group
      if (partNumber) {
        partGroup.userData.pn = partNumber;
        partGroup.userData.partNumber = partNumber;
        if (partGroup.userData.properties) {
          partGroup.userData.properties["Part Number"] = partNumber;
        }
      }
      if (mfgPart) {
        if (partGroup.userData.properties) {
          partGroup.userData.properties["Manufacturer Part Number"] = mfgPart;
        }
      }

      // Load font and update geometries
      const loader = new FontLoader();
      loader.load(
        "https://threejs.org/examples/fonts/helvetiker_regular.typeface.json",
        (font) => {
          const textProps = {
            font: font,
            size: 0.15,
            depth: 0.0001,
            curveSegments: 12,
          };

          // Update part number text if it exists and value changed
          if (partNumber && textMeshes.partNumber) {
            const newGeometry = new TextGeometry(partNumber, textProps);
            textMeshes.partNumber.geometry.dispose();
            textMeshes.partNumber.geometry = newGeometry;
            log("Updated part number text to:", partNumber);
          }

          // Update manufacturer part number text if it exists and value changed
          if (mfgPart && textMeshes.mfgPart) {
            const newGeometry = new TextGeometry(mfgPart, textProps);
            textMeshes.mfgPart.geometry.dispose();
            textMeshes.mfgPart.geometry = newGeometry;
            log("Updated manufacturer part number text to:", mfgPart);
          }
        },
        undefined,
        (error) => {
          console.error("Error loading font for text update:", error);
        }
      );
    } catch (error) {
      console.error("Error updating text meshes:", error);
    }
  }
}
